import React, { useEffect } from 'react'
import StandardTextInput from '../inputs/StandardTextInput';
import { useTranslation } from 'react-i18next';
import StandardSelect, { SelectOption } from '../inputs/StandardSelect';
import PrimaryButton from '../buttons/PrimaryButton';
import { countriesWithIsoCode } from '../models/Country';
import { currencyCodes } from '../models/Currency';
import { europeanTimezones } from '../models/Timezones';
import useFetch from '../hooks/useFetch';
import BigLoader from '../common/BigLoader';
import { postCreateMozrestVenue } from '../config/apiRoutes';
import UserContext from '../contexts/UserContext';

type Props = {
    onCreated: () => void;
}

const currencyCodeOptions = currencyCodes.map(code => ({ value: code, label: code }));
const timezoneOptions = europeanTimezones.map(tz => ({ value: tz, label: tz }));

const CreateVenueForm = (props: Props) => {
    const { selectedRestaurant } = React.useContext(UserContext);
    const { t } = useTranslation();
    const [selectedCountryOption, setSelectedCountryOption] = React.useState<SelectOption | null>(countriesWithIsoCode.find(c => c.value === "HU") || null);
    const [city, setCity] = React.useState("");
    const [address, setAddress] = React.useState("");
    const [postalCode, setPostalCode] = React.useState("");
    const [selectedCurrencyOption, setSelectedCurrencyOption] = React.useState<SelectOption | null>(currencyCodeOptions.find(c => c.value === "HUF") || null);
    const [selectedTimezone, setSelectedTimezone] = React.useState<SelectOption | null>(timezoneOptions.find(tz => tz.value === "Europe/Budapest") || null);
    const caller = useFetch();
    const [showValidation, setShowValidation] = React.useState(false);
    
    function isSaveAllowed(){
        return city.trim() !== "" && address.trim() !== "" && postalCode.trim() !== "" && selectedCountryOption != null && selectedCurrencyOption != null && selectedTimezone != null;
    }

    function handleCountryChange(option: SelectOption | null) {
        setSelectedCountryOption(option);
    }

    async function handleSave() {
        setShowValidation(true);
        if(!isSaveAllowed()) return;
        if (selectedRestaurant == null || selectedCurrencyOption == null || selectedTimezone == null || selectedCountryOption == null) return;
        const response = await caller.call<{ city: string, address: string, postalCode: string, currency: string, timezone: string, country: string }, any>("POST", postCreateMozrestVenue.replace(":restaurantShortName", selectedRestaurant.restaurantShortName), {
            city,
            address,
            postalCode,
            currency: selectedCurrencyOption?.value,
            timezone: selectedTimezone?.value,
            country: selectedCountryOption?.value,
        });
        if (response.ok) {
            props.onCreated();
        }
    }

    return (
        <div className="">
            <div className="text-center font-bold text-[20px] mb-2 text-primary-dark">
                {t("menu.review_manager_set_title")}
            </div>
            <div className="text-center text-[14px] text-primary-dark">
                {(t("menu.review_manager_set_description"))}
            </div>
            <div className="pt-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="col-span-1">
                    <StandardSelect
                        label={t("common.country")}
                        options={countriesWithIsoCode}
                        selectedOption={selectedCountryOption}
                        setSelectedOption={handleCountryChange} />
                </div>
                <div className="col-span-1">
                    <StandardTextInput
                        label={t("common.city")}
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        helperText={t("common.validation.required")}
                        error={showValidation && city.trim() === ""}
                        required
                    />
                </div>
                <div className="col-span-1">
                    <StandardTextInput
                        label={t("common.address")}
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        helperText={t("common.validation.required")}
                        error={showValidation && address.trim() === ""}
                        required
                    />
                </div>
                <div className="col-span-1">
                    <StandardTextInput
                        label={t("common.postal_code")}
                        value={postalCode}
                        onChange={(e) => setPostalCode(e.target.value)}
                        error={showValidation && postalCode.trim() === ""}
                        required
                        helperText={t("common.validation.required")}
                    />
                </div>
                <div className="col-span-1">
                    <StandardSelect
                        label={t("common.currency")}
                        options={currencyCodeOptions}
                        selectedOption={selectedCurrencyOption}
                        setSelectedOption={setSelectedCurrencyOption}
                        helperText={t("common.currency_tooltip")}
                    />
                </div>
                <div className="col-span-1">
                    <StandardSelect
                        label={t("common.timezone")}
                        options={timezoneOptions}
                        selectedOption={selectedTimezone}
                        setSelectedOption={setSelectedTimezone}
                    />
                </div>
            </div>
            <div className="mt-4">
                {!caller.loading && (
                    <PrimaryButton
                        variant="strike"
                        fullWidthOnMobile
                        onClick={() => handleSave()}
                        disabled={!isSaveAllowed() && showValidation}
                    >
                        {t("menu.confirm_settings")}
                    </PrimaryButton>
                )}
                {caller.loading && (
                    <div className="text-center">
                        <BigLoader />
                    </div>
                )}
            </div>
        </div>
    )
}

export default CreateVenueForm